import { useState, useEffect } from "react";
import { useSession } from "../context/AuthContext";
import { useNavigate, useParams } from "react-router-dom";
import Header from "../components/layout/Header";
import Sidebar from "../components/layout/Sidebar";
import api from "../services/api";
import { usePlans } from "../context/PlanContext";

interface ProgressData {
  id: string;
  name: string;
  email: string;
  membership: string;
  points: number;
  submissions: any[];
  userBadges: any[];
  pointHistory: any[];
}

export default function StudentDetailPage() {
  const { status } = useSession();
  const { getPlanByName } = usePlans();
  const navigate = useNavigate();
  const { id } = useParams();
  const [data, setData] = useState<ProgressData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (status === "unauthenticated") {
      navigate("/login");
    }
  }, [status, navigate]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get(`/coach/students/${id}/progress`);
        const json = res.data;
        setData({
          id: json.student.id,
          name: json.student.name,
          email: json.student.email,
          membership: json.student.membership,
          points: json.student.points,
          submissions: json.submissions || [],
          userBadges: json.badges || [],
          pointHistory: json.activeTracks || []
        });
      } catch (err) {
        console.error("Failed to fetch student details:", err);
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchData();
  }, [id]);

  if (status === "loading" || loading) return null;
  if (!data) return <div className="p-20 text-center font-bold uppercase tracking-widest opacity-30">Student not found.</div>;

  return (
    <div className="min-h-screen bg-background text-on-background flex flex-col">
      <Header />
      <Sidebar />

      <main className="flex-1 ml-0 md:ml-20 pt-16">
        <div className="max-w-7xl mx-auto p-4 sm:p-8 lg:p-12">
          {/* Back Button */}
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.2em] text-on-surface-variant/40 hover:text-primary transition-colors mb-6 sm:mb-8"
          >
            <span className="material-symbols-outlined text-lg">arrow_back</span>
            Back to Assessment
          </button>

          {/* Profile Header */}
          <div className="bg-surface-container-low rounded-[1.75rem] sm:rounded-[3rem] p-6 sm:p-10 border border-outline-variant/10 mb-8 sm:mb-12 shadow-2xl shadow-black/5">
            <div className="flex flex-col md:flex-row items-center gap-6 sm:gap-8 text-center md:text-left">
              <div className="w-20 h-20 sm:w-28 sm:h-28 rounded-[1.5rem] sm:rounded-[2rem] bg-on-surface text-surface flex items-center justify-center font-black text-3xl sm:text-4xl shadow-2xl ring-4 sm:ring-8 ring-surface-container-highest flex-shrink-0 animate-in spin-in-12 duration-500">
                {data?.name?.[0] || "U"}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-col md:flex-row md:items-center gap-3 sm:gap-4 mb-3 justify-center md:justify-start">
                  <h1 className="text-2xl sm:text-4xl font-black text-on-surface tracking-tighter truncate">{data.name}</h1>
                  <span 
                    className="text-[10px] font-black px-4 py-1.5 rounded-full uppercase tracking-[0.2em] w-fit mx-auto md:mx-0 shadow-sm"
                    style={{
                      backgroundColor: getPlanByName(data.membership)?.icon_color || (
                        data.membership === "VIP" ? "#06b6d4" : 
                        data.membership === "PREMIUM" ? "#f59e0b" : 
                        "#64748b"
                      ),
                      color: "#ffffff"
                    }}
                  >
                    {data.membership}
                  </span>
                </div>
                <p className="text-on-surface-variant font-medium text-xs sm:text-sm opacity-60 mb-6 truncate">{data.email}</p>
                
                <div className="flex flex-wrap gap-3 sm:gap-4 justify-center md:justify-start">
                  <div className="bg-surface-container-highest/50 px-4 sm:px-6 py-2.5 sm:py-3 rounded-2xl border border-outline-variant/10">
                    <p className="text-[9px] font-black uppercase tracking-widest text-on-surface-variant/40 mb-1">Total Points</p>
                    <p className="text-base sm:text-lg font-black text-on-surface">{data.points.toLocaleString()}</p>
                  </div>
                  <div className="bg-surface-container-highest/50 px-4 sm:px-6 py-2.5 sm:py-3 rounded-2xl border border-outline-variant/10">
                    <p className="text-[9px] font-black uppercase tracking-widest text-on-surface-variant/40 mb-1">Badges Earned</p>
                    <p className="text-base sm:text-lg font-black text-on-surface">{data.userBadges.length}</p>
                  </div>
                  <div className="bg-surface-container-highest/50 px-4 sm:px-6 py-2.5 sm:py-3 rounded-2xl border border-outline-variant/10">
                    <p className="text-[9px] font-black uppercase tracking-widest text-on-surface-variant/40 mb-1">Tasks Completed</p>
                    <p className="text-base sm:text-lg font-black text-on-surface">{data.submissions.length}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 sm:gap-10">
            {/* Left Col: Task Submissions */}
            <div className="lg:col-span-2 space-y-8 sm:space-y-10">
              <section>
                <div className="flex items-center justify-between mb-6 px-4">
                  <h3 className="text-lg sm:text-xl font-black text-on-surface tracking-tighter">Recent Submissions</h3>
                  <span className="text-[10px] font-bold text-on-surface-variant/40 uppercase tracking-widest">{data.submissions.length} Total</span>
                </div>
                
                <div className="space-y-4">
                  {(() => {
                    // Deduplicate submissions by taskId, keeping the most recent
                    const uniqueSubs = Array.from(
                      data.submissions.reduce((map: any, sub: any) => {
                        if (!map.has(sub.taskId) || new Date(sub.created_at || sub.createdAt) > new Date(map.get(sub.taskId).created_at || map.get(sub.taskId).createdAt)) {
                          map.set(sub.taskId, sub);
                        }
                        return map;
                      }, new Map()).values()
                    );

                    return uniqueSubs.length > 0 ? uniqueSubs.slice(0, 5).map((sub: any) => (
                      <div key={sub.id} className="bg-surface-container-low p-5 sm:p-6 rounded-2xl sm:rounded-[2rem] border border-outline-variant/10 hover:shadow-xl transition-all group">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-start gap-3 sm:gap-4 min-w-0">
                            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl bg-surface-container-high flex items-center justify-center text-primary flex-shrink-0">
                              <span className="material-symbols-outlined">{sub.task?.type === 'IMAGE' ? 'image' : 'article'}</span>
                            </div>
                            <div className="min-w-0">
                              <h4 className="text-sm font-black text-on-surface mb-1 group-hover:text-primary transition-colors truncate">{sub.task?.title || "Unnamed Task"}</h4>
                              <p className="text-[10px] font-medium text-on-surface-variant/60 uppercase tracking-wider">
                                Submitted {new Date(sub.created_at || sub.updated_at || sub.createdAt).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <span className={`text-[8px] font-black px-2.5 py-1 rounded-full uppercase tracking-widest flex-shrink-0 ${
                            sub.status === "APPROVED" ? "bg-emerald-500/10 text-emerald-500 border border-emerald-500/10" :
                            sub.status === "REJECTED" ? "bg-red-500/10 text-red-500 border border-red-500/10" :
                            "bg-amber-500/10 text-amber-500 border border-amber-500/10"
                          }`}>
                            {sub.status}
                          </span>
                        </div>
                      </div>
                    )) : (
                      <div className="p-12 text-center bg-surface-container-low rounded-2xl sm:rounded-[2rem] border border-dashed border-outline-variant/20 opacity-30">
                        <p className="text-xs font-bold uppercase tracking-widest">No submissions yet</p>
                      </div>
                    );
                  })()}
                </div>
              </section>

              <section>
                <div className="flex items-center justify-between mb-6 px-4">
                  <h3 className="text-lg sm:text-xl font-black text-on-surface tracking-tighter">Point History</h3>
                </div>
                <div className="bg-surface-container-low rounded-[1.5rem] sm:rounded-[2.5rem] border border-outline-variant/10 overflow-hidden shadow-sm overflow-x-auto">
                  <table className="w-full text-left min-w-[500px]">
                    <thead>
                      <tr className="bg-surface-container-high/40 text-[9px] font-black uppercase tracking-widest text-on-surface-variant/40 border-b border-outline-variant/10">
                        <th className="px-6 py-3">Reason</th>
                        <th className="px-6 py-3 text-center">XP</th>
                        <th className="px-6 py-3 text-right">Date</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-outline-variant/5">
                      {data.pointHistory.map((log: any) => (
                        <tr key={log.id} className="hover:bg-primary/5 transition-colors">
                          <td className="px-6 py-4 text-xs font-medium text-on-surface-variant">{log.reason}</td>
                          <td className="px-6 py-4 text-center">
                            <span className={`text-xs font-black ${log.points >= 0 ? "text-emerald-500" : "text-red-500"}`}>
                              {log.points >= 0 ? "+" : ""}{log.points} XP
                            </span>
                          </td>
                          <td className="px-6 py-4 text-right text-[10px] font-bold text-on-surface-variant/40 uppercase tracking-widest">
                            {new Date(log.createdAt).toLocaleDateString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            </div>

            {/* Right Col: Badges & Profile Details */}
            <div className="space-y-8 sm:space-y-10">
              <section>
                <div className="flex items-center justify-between mb-6 px-4">
                  <h3 className="text-lg sm:text-xl font-black text-on-surface tracking-tighter">Achievements</h3>
                </div>
                <div className="grid grid-cols-2 gap-3 sm:gap-4">
                  {data.userBadges.length > 0 ? data.userBadges.map((ub: any) => (
                    <div key={ub.id} className="bg-surface-container-low p-4 rounded-xl sm:rounded-[1.5rem] border border-outline-variant/10 text-center group hover:scale-105 transition-all">
                      <div className="text-2xl sm:text-3xl mb-2 group-hover:animate-bounce h-10 flex items-center justify-center">
                        {ub.badge?.icon?.length > 2 ? (
                          <span className="material-symbols-outlined text-2xl sm:text-3xl">{ub.badge.icon}</span>
                        ) : (
                          ub.badge?.icon || "star"
                        )}
                      </div>
                      <p className="text-[9px] font-black uppercase tracking-widest text-on-surface truncate">{ub.badge?.name || "Legacy Badge"}</p>
                    </div>
                  )) : (
                    <div className="col-span-2 p-10 text-center bg-surface-container-low rounded-xl sm:rounded-[1.5rem] opacity-30">
                      <p className="text-[10px] font-bold uppercase tracking-widest">No badges earned</p>
                    </div>
                  )}
                </div>
              </section>

              <div className="bg-primary/10 p-6 sm:p-8 rounded-[1.75rem] sm:rounded-[2.5rem] border border-primary/20">
                <h4 className="text-base sm:text-lg font-black text-primary tracking-tighter mb-4">Coach Summary</h4>
                <p className="text-xs text-primary/80 font-medium leading-relaxed mb-6">
                  {data.name} is currently a <span className="font-black">{data.membership}</span> member with <span className="font-black">{data.points}</span> accumulated points. 
                  {data.submissions.filter(s => s.status === 'APPROVED').length} tasks have been successfully validated.
                </p>
                <div className="space-y-3">
                  <div className="w-full bg-primary/20 h-2 rounded-full overflow-hidden">
                    <div 
                      className="bg-primary h-full transition-all duration-1000" 
                      style={{ width: `${Math.min(100, (data.points / 5000) * 100)}%` }} 
                    />
                  </div>
                  <p className="text-[9px] font-black uppercase tracking-widest text-primary/60 text-right">Progress to Next Tier</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
